<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToScroll'
});
</script>
<template>
	<div class="to-scroll" :flex="flex ? flex : null" :class="setClass" :style="setStyle">
		<!-- 自定义滚动条 -->
		<div v-if="props.virtual" class="to-scroll-bar" :class="[barVisibleClass, { active: isScrolling || isHovering }]">
			<div ref="barThumb" class="to-scroll-thumb" :style="thumbStyle" @mousedown="onThumbMousedown"></div>
		</div>

		<!-- 横向滚动条 -->
		<div v-if="props.virtual && hasHorizontalScroll" class="to-scroll-bar horizontal" :class="[barVisibleClass, { active: isScrolling || isHovering }]">
			<div ref="horizontalBarThumb" class="to-scroll-thumb" :style="horizontalThumbStyle" @mousedown="onHorizontalThumbMousedown"></div>
		</div>

		<!-- 滚动内容区域 -->
		<div ref="scrollRef" class="to-scroll-content" :style="setContentStyle" :class="{ 'virtual-scroll': props.virtual }" @scroll="handleScroll">
			<!-- 默认内容插槽 -->
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onUnmounted, provide } from 'vue'
import { scrollProps, ScrollContext } from './scroll'



const props = defineProps(scrollProps)
const emit = defineEmits<{
	(e: 'scroll', event: Event): void
	(e: 'scrollEnd'): void
	(e: 'change', position: number): void
	(e: 'update:value', position: number | string): void
	(e: 'update:modelValue', position: number | string): void
	(e: 'bottom'): void
	(e: 'top'): void
}>()

const scrollRef = ref<HTMLElement | null>(null)
const barThumb = ref<HTMLElement | null>(null)
const horizontalBarThumb = ref<HTMLElement | null>(null)
const isScrolling = ref(false)
const isHovering = ref(false)
// 添加一个标志来标识是否由tab点击导致的滚动
let isTabTriggeredScroll = false
// 添加自动滚动的节流控制
let autoScrollThrottleTimer: ReturnType<typeof setTimeout> | null = null
// 滚动防抖定时器
let scrollUpdateTimer: ReturnType<typeof setTimeout> | null = null
let isDragging = ref(false)
let isHorizontalDragging = ref(false)
let startY = 0
let startX = 0
let startScrollTop = 0
let startScrollLeft = 0
const hasHorizontalScroll = ref(false)
// 存储滚动点信息
const scrollPoints = ref<Array<{ value: string | number; start: number; end: number }>>([])
// 获取当前绑定值（优先使用modelValue，兼容value）
const bindValue = computed(() => props.modelValue || props.value)
// 当前选中的滚动点值
const currentPointValue = ref<string | number | null>(bindValue.value || null)

let scrollEventDebounceTimer: ReturnType<typeof setTimeout> | null = null
// 提供给子组件的上下文
provide<ScrollContext>('ToScroll', {
	registerScrollPoint: (_el: HTMLElement, _value: string | number) => {
		// 在下一个渲染周期更新滚动点
		nextTick(() => {
			setScrollPoints()
		})
	}
})

// 提供当前值给子组件
provide('scrollValue', currentPointValue)

// 计算属性
const barVisibleClass = computed(() => {
	return `bar-${String(props.barDisplay)}`
})

// 检查是否需要横向滚动条
const checkHorizontalScroll = () => {
	if (scrollRef.value) {
		const { scrollWidth, clientWidth } = scrollRef.value
		hasHorizontalScroll.value = scrollWidth > clientWidth
	}
}

// 设置滚动点
const setScrollPoints = () => {
	if (!scrollRef.value) return

	// 先清空现有的滚动点
	scrollPoints.value = []

	// 查找所有滚动点元素
	const nodes = scrollRef.value.querySelectorAll('.to-scroll-point')
	if (!nodes || nodes.length === 0) {
		return
	}

	// 收集滚动点信息
	nodes.forEach(item => {
		const itemEl = item as HTMLElement
		// 获取滚动点的值
		const pointValue = itemEl.getAttribute('data-point-value') || ''

		// 获取元素相对于滚动容器的精确位置
		const offsetTop = itemEl.offsetTop
		const height = itemEl.offsetHeight

		// 记录滚动点信息
		scrollPoints.value.push({
			value: pointValue,
			start: offsetTop,
			end: offsetTop + height
		})
	})

	// 初始滚动到指定位置
	if (props.value && scrollRef.value) {
		scrollToValue(props.value, 'auto')
	}
}

// 滚动到指定值对应的位置
const scrollToValue = (value: string | number, behavior: ScrollBehavior = 'smooth') => {
	if (!scrollRef.value || !scrollPoints.value.length) return false

	// 查找匹配值的滚动点
	const targetPoint = scrollPoints.value.find(p => String(p.value) === String(value))
	if (targetPoint) {
		scrollRef.value.scrollTo({
			top: targetPoint.start,
			behavior: behavior
		})

		// 主动触发滚动点高亮状态更新
		notifyScrollPointsActive(value)
		return true
	}

	return false
}

// 主动通知滚动点更新高亮状态
const notifyScrollPointsActive = (activeValue: string | number) => {
	// 使用事件通知，避免直接操作DOM
	if (scrollRef.value) {
		const event = new CustomEvent('scroll-point-active', {
			detail: { value: activeValue },
			bubbles: true
		})
		scrollRef.value.dispatchEvent(event)

		// 确保更新内部状态
		currentPointValue.value = activeValue
	}
}

// 根据当前滚动位置查找对应的滚动点值
const findScrollPoint = (scrollTop: number): string | number | null => {
	if (!scrollPoints.value.length || !scrollRef.value) return null

	const clientHeight = scrollRef.value.clientHeight
	const scrollBottom = scrollTop + clientHeight

	// 1. 精确匹配：如果滚动位置恰好在某个滚动点的顶部附近
	for (const point of scrollPoints.value) {
		// 如果滚动位置在点顶部的±10px范围内，优先选择该点
		if (Math.abs(scrollTop - point.start) < 10) {
			return point.value
		}
	}

	// 2. 完全包含的滚动点：如果视口完全包含某个滚动点
	for (const point of scrollPoints.value) {
		if (scrollTop <= point.start && scrollBottom >= point.end) {
			return point.value
		}
	}

	// 3. 查找当前在视口中显示最多的点
	let bestPoint = null
	let maxVisibleHeight = 0

	for (const point of scrollPoints.value) {
		// 计算可见部分
		const visibleTop = Math.max(scrollTop, point.start)
		const visibleBottom = Math.min(scrollBottom, point.end)
		const visibleHeight = visibleBottom - visibleTop

		// 如果有可见部分，并且可见高度最大，选择该点
		if (visibleHeight > 0 && visibleHeight > maxVisibleHeight) {
			maxVisibleHeight = visibleHeight
			bestPoint = point
		}
	}

	// 4. 特殊情况处理：滚动到顶部或底部
	if (scrollTop < 5 && scrollPoints.value.length > 0) {
		return scrollPoints.value[0].value // 顶部
	}

	if (scrollBottom >= scrollRef.value.scrollHeight - 5 && scrollPoints.value.length > 0) {
		return scrollPoints.value[scrollPoints.value.length - 1].value // 底部
	}

	// 5. 如果找到了最佳点，返回它的值
	if (bestPoint) {
		return bestPoint.value
	}

	// 6. 后备方案：返回最接近当前滚动位置的点
	let closestPoint = null
	let minDistance = Infinity

	for (const point of scrollPoints.value) {
		const distance = Math.min(Math.abs(scrollTop - point.start), Math.abs(scrollBottom - point.end))

		if (distance < minDistance) {
			minDistance = distance
			closestPoint = point
		}
	}

	return closestPoint ? closestPoint.value : null
}

// 监听value/modelValue变化
watch(
	() => bindValue.value,
	(newVal, oldVal) => {
		// 避免重复处理相同的值
		if (newVal === oldVal || newVal === currentPointValue.value) return

		// 更新内部记录的值
		currentPointValue.value = newVal

		// 如果是由Tab变化引起的，滚动到对应位置
		if (!isScrolling.value) {
			// 标记为Tab触发的滚动，避免滚动过程中再次触发更新
			isTabTriggeredScroll = true

			nextTick(() => {
				// 尝试滚动到对应位置
				const scrollSuccess = scrollToValue(newVal)

				// 如果滚动失败（没找到对应的滚动点），尝试重新计算滚动点后再滚动
				if (!scrollSuccess) {
					setScrollPoints()
					scrollToValue(newVal)
				}

				// 滚动动作完成后重置标记
				setTimeout(() => {
					isTabTriggeredScroll = false
				}, 500) // 等待滚动动画完成
			})
		}
	},
	{ immediate: true }
)

onMounted(() => {
	// 组件挂载后，设置滚动点
	nextTick(() => {
		// 收集滚动点
		setScrollPoints()

		// 设置观察器，监听DOM变化重新收集滚动点
		const observer = new MutationObserver(() => {
			setScrollPoints()
		})

		if (scrollRef.value) {
			observer.observe(scrollRef.value, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['data-point-value']
			})

			// 如果有初始值，滚动到对应位置
			if (props.value) {
				// 延迟执行，确保DOM完全渲染
				setTimeout(() => {
					if (typeof props.value === 'string' || typeof props.value === 'number') {
						scrollToValue(props.value, 'auto')
					}
				}, 100)
			}
		}
	})

	if (props.virtual) {
		window.addEventListener('mousemove', onMousemove)
		window.addEventListener('mouseup', onMouseup)
		window.addEventListener('mouseleave', onMouseup)

		// 添加鼠标进入和离开事件
		if (scrollRef.value) {
			scrollRef.value.addEventListener('mouseenter', onMouseenter)
			scrollRef.value.addEventListener('mouseleave', onMouseleave)
		}

		// 检查是否需要横向滚动条
		nextTick(() => {
			checkHorizontalScroll()
		})
	}
})

onUnmounted(() => {
	if (props.virtual) {
		window.removeEventListener('mousemove', onMousemove)
		window.removeEventListener('mouseup', onMouseup)
		window.removeEventListener('mouseleave', onMouseup)

		// 移除鼠标进入和离开事件
		if (scrollRef.value) {
			scrollRef.value.removeEventListener('mouseenter', onMouseenter)
			scrollRef.value.removeEventListener('mouseleave', onMouseleave)
		}
	}

	// 清除所有定时器
	if (scrollUpdateTimer) {
		clearTimeout(scrollUpdateTimer)
		scrollUpdateTimer = null
	}

	if (autoScrollThrottleTimer) {
		clearTimeout(autoScrollThrottleTimer)
		autoScrollThrottleTimer = null
	}
})

// 鼠标进入滚动区域
const onMouseenter = () => {
	isHovering.value = true
}

// 鼠标离开滚动区域
const onMouseleave = () => {
	if (!isDragging.value && !isHorizontalDragging.value) {
		isHovering.value = false
	}
}

// 虚拟滚动条垂直拖动
const onThumbMousedown = (e: MouseEvent) => {
	if (!scrollRef.value || !props.virtual) return
	e.preventDefault()
	e.stopPropagation()

	// 添加拖动状态的类，用于CSS样式调整
	if (scrollRef.value) {
		scrollRef.value.classList.add('dragging-scrollbar')
	}

	startY = e.clientY
	startScrollTop = scrollRef.value.scrollTop
	isDragging.value = true
}

// 虚拟滚动条水平拖动
const onHorizontalThumbMousedown = (e: MouseEvent) => {
	if (!scrollRef.value || !props.virtual) return
	e.preventDefault()
	e.stopPropagation()

	// 添加拖动状态的类，用于CSS样式调整
	if (scrollRef.value) {
		scrollRef.value.classList.add('dragging-scrollbar-horizontal')
	}

	startX = e.clientX
	startScrollLeft = scrollRef.value.scrollLeft
	isHorizontalDragging.value = true
}

const onMousemove = (e: MouseEvent) => {
	// 处理垂直拖动
	if (isDragging.value && scrollRef.value && barThumb.value) {
		const deltaY = e.clientY - startY
		const contentHeight = scrollRef.value.scrollHeight
		const containerHeight = scrollRef.value.clientHeight
		const ratio = containerHeight / contentHeight

		scrollRef.value.scrollTop = startScrollTop + deltaY / ratio
	}

	// 处理水平拖动
	if (isHorizontalDragging.value && scrollRef.value && horizontalBarThumb.value) {
		const deltaX = e.clientX - startX
		const contentWidth = scrollRef.value.scrollWidth
		const containerWidth = scrollRef.value.clientWidth
		const ratio = containerWidth / contentWidth

		scrollRef.value.scrollLeft = startScrollLeft + deltaX / ratio
	}
}

const onMouseup = () => {
	isDragging.value = false
	isHorizontalDragging.value = false

	// 拖动结束后，如果鼠标不在滚动区域内，则隐藏滚动条
	if (!isHovering.value) {
		setTimeout(() => {
			isHovering.value = false
		}, 300)
	}

	// 移除拖动状态的类
	if (scrollRef.value) {
		scrollRef.value.classList.remove('dragging-scrollbar')
		scrollRef.value.classList.remove('dragging-scrollbar-horizontal')
	}
}

// 处理滚动事件
const handleScroll = (event: Event) => {
	// 触发原始滚动事件
	emit('scroll', event)

	// 如果是由Tab触发的滚动，不处理滚动检测
	if (isTabTriggeredScroll) {
		return
	}

	if (!scrollRef.value) return

	// 获取当前滚动位置
	const scrollTop = scrollRef.value.scrollTop

	// 触发滚动位置变化事件
	emit('change', scrollTop)

	if (scrollEventDebounceTimer) {
		clearTimeout(scrollEventDebounceTimer)
	}

	// 检测是否滚动到底部
	scrollEventDebounceTimer = setTimeout(() => {
		// 检测是否滚动到底部
		const { scrollHeight, clientHeight } = scrollRef.value
		if (scrollTop + clientHeight >= scrollHeight - 5) {
			emit('bottom')
		}

		if (scrollTop <= 5) {
			emit('top')
		}
	}, 100) // 防抖时间设置为 100ms

	// 如果有滚动点，立即检测当前位置对应哪个点
	if (scrollPoints.value.length > 0) {
		// 先清除之前的定时器
		if (autoScrollThrottleTimer) {
			clearTimeout(autoScrollThrottleTimer)
			autoScrollThrottleTimer = null
		}
		if (scrollUpdateTimer) {
			clearTimeout(scrollUpdateTimer)
			scrollUpdateTimer = null
		}

		// 使用requestAnimationFrame确保滚动过程中能平滑更新
		requestAnimationFrame(() => {
			// 重新查找当前滚动位置对应的点
			const pointValue = findScrollPoint(scrollTop)

			// 如果找到点且与当前值不同，立即更新值
			if (pointValue !== null && pointValue !== currentPointValue.value) {
				// 更新内部记录的值
				currentPointValue.value = pointValue

				// 同时发送两种事件，确保兼容性
				emit('update:modelValue', pointValue)
				emit('update:value', pointValue)

				// 通知滚动点更新高亮状态
				notifyScrollPointsActive(pointValue)

				// 如果使用了虚拟滚动条，确保它的位置也正确更新
				if (props.virtual && barThumb.value) {
					updateVirtualScrollbar()
				}
			}
		})
	}

	// 标记为正在滚动
	isScrolling.value = true

	// 滚动结束后重置状态
	clearTimeout(scrollUpdateTimer)
	scrollUpdateTimer = setTimeout(() => {
		isScrolling.value = false
		emit('scrollEnd')
	}, 100)
}

// 更新虚拟滚动条位置（如果使用）
const updateVirtualScrollbar = () => {
	if (!scrollRef.value || !props.virtual) return

	// 强制更新滚动条位置
	nextTick(() => {
		const { scrollTop, scrollHeight, clientHeight } = scrollRef.value
		const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * clientHeight)
		const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight)

		if (barThumb.value) {
			barThumb.value.style.transform = `translateY(${thumbTop}px)`
		}
	})
}

// 统一的滚动方法，支持多种参数类型
const to = (target: HTMLElement | string | number | any) => {
	if (!scrollRef.value) return

	// 滚动到底部
	if (target === '$bottom') {
		scrollRef.value.scrollTo({
			top: scrollRef.value.scrollHeight - scrollRef.value.clientHeight,
			behavior: props.virtual ? 'smooth' : 'auto'
		})
		return
	}

	// 滚动到指定元素
	if (target instanceof HTMLElement) {
		const elementTop = target.getBoundingClientRect().top
		const containerTop = scrollRef.value.getBoundingClientRect().top
		const relativeTop = elementTop - containerTop + scrollRef.value.scrollTop

		scrollRef.value.scrollTo({
			top: relativeTop,
			behavior: props.virtual ? 'smooth' : 'auto'
		})
		return
	}

	// 滚动到指定位置（数字或数字字符串）
	if (typeof target === 'number' || (typeof target === 'string' && !isNaN(Number(target)))) {
		const position = typeof target === 'string' ? Number(target) : target

		scrollRef.value.scrollTo({
			top: position,
			left: 0,
			behavior: props.virtual ? 'smooth' : 'auto'
		})
		return
	}

	// 滚动到指定选项配置
	if (typeof target === 'object' && target !== null) {
		scrollRef.value.scrollTo(target)
		return
	}
}

// 垂直滚动条样式计算
const thumbStyle = computed(() => {
	if (!scrollRef.value || !props.virtual) return {}

	const { clientHeight, scrollHeight, scrollTop } = scrollRef.value

	// 计算滚动条高度比例
	const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * clientHeight)

	// 计算滚动条位置比例
	const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight)

	return {
		height: `${thumbHeight}px`,
		transform: `translateY(${thumbTop}px)`,
		width: '6px'
	}
})

// 水平滚动条样式计算
const horizontalThumbStyle = computed(() => {
	if (!scrollRef.value || !props.virtual) return {}

	const { clientWidth, scrollWidth, scrollLeft } = scrollRef.value

	// 计算滚动条宽度比例
	const thumbWidth = Math.max(30, (clientWidth / scrollWidth) * clientWidth)

	// 计算滚动条位置比例
	const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * (clientWidth - thumbWidth)

	return {
		width: `${thumbWidth}px`,
		transform: `translateX(${thumbLeft}px)`,
		height: '6px'
	}
})

const setStyle = computed(() => {
	const obj: {
		height?: string
	} = {}

	if (props.height) {
		if (!isNaN(Number(props.height))) {
			obj.height = props.height + 'em'
		} else {
			obj.height = String(props.height)
		}
	}

	return obj
})

const setClass = computed(() => {
	let arr: string[] = []

	if (props.virtual) {
		arr.push('hide-scrollbar')
	}

	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}

	if (props.type) {
		arr.push(`type-${props.type}`)
	}

	return arr
})

const setContentStyle = computed(() => {
	const obj: {
		height?: string
		padding?: string | number
		paddingTop?: string
		paddingRight?: string
		paddingBottom?: string
		paddingLeft?: string
		maxHeight?: string
		overflow?: string
		position?: 'relative' | 'absolute' | 'fixed' | 'sticky'
		width?: string
		display?: string
	} = {}

	const hasUnit = (val: string) => /[a-z%]+$/i.test(val)
	if (String(props.padding).trim()) {
		// 分割成各个值
		const padded = String(props.padding)
			.trim()
			.split(/\s+/)
			.map(val => {
				// 如果是数字（或纯数字字符串），或末尾无单位，就加 em
				return hasUnit(val) ? val : `${val}em`
			})
			.join(' ')

		obj.padding = padded
	}

	if (props.paddingPosition) {
		// 根据paddingPosition设置padding位置
		const padding = String(props.padding || '0')
		const hasUnit = (val: string) => /[a-z%]+$/i.test(val)
		const paddingValue = hasUnit(padding) ? padding : `${padding}em`

		switch (props.paddingPosition) {
			case 'top':
				obj.paddingTop = paddingValue
				break
			case 'right':
				obj.paddingRight = paddingValue
				break
			case 'bottom':
				obj.paddingBottom = paddingValue
				break
			case 'left':
				obj.paddingLeft = paddingValue
				break
		}
	}

	if (props.virtual) {
		obj.overflow = 'auto'
		obj.position = 'relative'
		obj.width = '100%'
	}

	return obj
})

function adjust() {
	// 组件挂载后，设置滚动点
	nextTick(() => {
		// 收集滚动点
		setScrollPoints()

		// 设置观察器，监听DOM变化重新收集滚动点
		const observer = new MutationObserver(() => {
			setScrollPoints()
		})

		if (scrollRef.value) {
			observer.observe(scrollRef.value, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['data-point-value']
			})

			// 如果有初始值，滚动到对应位置
			if (props.value) {
				// 延迟执行，确保DOM完全渲染
				setTimeout(() => {
					if (typeof props.value === 'string' || typeof props.value === 'number') {
						scrollToValue(props.value, 'auto')
					}
				}, 100)
			}
		}
	})

	if (props.virtual) {
		window.addEventListener('mousemove', onMousemove)
		window.addEventListener('mouseup', onMouseup)
		window.addEventListener('mouseleave', onMouseup)

		// 添加鼠标进入和离开事件
		if (scrollRef.value) {
			scrollRef.value.addEventListener('mouseenter', onMouseenter)
			scrollRef.value.addEventListener('mouseleave', onMouseleave)
		}

		// 检查是否需要横向滚动条
		nextTick(() => {
			checkHorizontalScroll()
		})
	}
}

// 导出方法供父组件调用
defineExpose({
	to,
	scrollRef,
	isScrolling,
	setScrollPoints,
	findScrollPoint,
	checkHorizontalScroll,
	adjust
})
</script>
